import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------- ItemGroup */

const ItemGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} data-slot="item-group" className={cn('flex flex-col gap-3', className)} {...props} />
  ),
);
ItemGroup.displayName = 'ItemGroup';

interface ItemGroupHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
}

const ItemGroupHeader = React.forwardRef<HTMLDivElement, ItemGroupHeaderProps>(
  ({ title, description, action, className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-group-header"
      className={cn('flex flex-wrap items-start justify-between gap-2', className)}
      {...props}
    >
      <div className="min-w-0 space-y-0.5">
        <h2 className="text-xl font-semibold leading-tight tracking-tight">{title}</h2>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  ),
);
ItemGroupHeader.displayName = 'ItemGroupHeader';

const ItemGroupContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-group-content"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  ),
);
ItemGroupContent.displayName = 'ItemGroupContent';

const ItemGroupEmpty = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-group-empty"
      className={cn(
        'rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground',
        className,
      )}
      {...props}
    />
  ),
);
ItemGroupEmpty.displayName = 'ItemGroupEmpty';

/* ------------------------------------------------------------------------- Item */

const itemVariants = cva(
  'group/item relative flex w-full items-stretch gap-3 rounded-lg border bg-card p-3 text-card-foreground shadow-xs transition-colors',
  {
    variants: {
      interactive: {
        true: 'cursor-pointer hover:border-foreground/30 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        false: '',
      },
      active: {
        true: 'border-primary bg-accent/40',
        false: '',
      },
    },
    defaultVariants: {
      interactive: false,
      active: false,
    },
  },
);

interface ItemProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof itemVariants> {
  asChild?: boolean;
}

const Item = React.forwardRef<HTMLDivElement, ItemProps>(
  ({ className, interactive, active, asChild, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';
    const isInteractive = interactive ?? Boolean(onClick);
    return (
      <Comp
        ref={ref}
        data-slot="item"
        data-active={active || undefined}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        className={cn(itemVariants({ interactive: isInteractive, active }), className)}
        onClick={onClick}
        onKeyDown={
          isInteractive && onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  (onClick as unknown as (ev: typeof e) => void)(e);
                }
              }
            : undefined
        }
        {...props}
      />
    );
  },
);
Item.displayName = 'Item';

const ItemMedia = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-media"
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground [&_svg]:size-4',
        className,
      )}
      {...props}
    />
  ),
);
ItemMedia.displayName = 'ItemMedia';

const ItemContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-content"
      className={cn('flex min-w-0 flex-1 flex-col gap-1', className)}
      {...props}
    />
  ),
);
ItemContent.displayName = 'ItemContent';

const ItemHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-header"
      className={cn('flex min-w-0 flex-wrap items-center gap-1.5', className)}
      {...props}
    />
  ),
);
ItemHeader.displayName = 'ItemHeader';

const ItemTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-title"
      className={cn('truncate text-sm font-semibold leading-tight', className)}
      {...props}
    />
  ),
);
ItemTitle.displayName = 'ItemTitle';

const ItemDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-description"
      className={cn('text-xs leading-snug text-muted-foreground', className)}
      {...props}
    />
  ),
);
ItemDescription.displayName = 'ItemDescription';

const ItemActions = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, onClick, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-actions"
      // Stop propagation so clicking action buttons doesn't fire the parent
      // Item's onClick handler.
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      className={cn('flex shrink-0 items-center gap-1.5 self-center', className)}
      {...props}
    />
  ),
);
ItemActions.displayName = 'ItemActions';

const ItemFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-footer"
      className={cn('mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground', className)}
      {...props}
    />
  ),
);
ItemFooter.displayName = 'ItemFooter';

export {
  ItemGroup,
  ItemGroupHeader,
  ItemGroupContent,
  ItemGroupEmpty,
  Item,
  ItemMedia,
  ItemContent,
  ItemHeader,
  ItemTitle,
  ItemDescription,
  ItemActions,
  ItemFooter,
};
